import { useMemo } from 'react';
import { ImagePair } from '@/data/galleryData';
import { ImageReveal } from '@/components/ImageReveal';
import { VideoPlayer } from '@/components/VideoPlayer';
import { getResponsiveProps } from '@/lib/responsiveImage';

interface VerticalGalleryProps {
  pairs: ImagePair[];
  sectionTitle?: string;
}

type VerticalItem =
  | { type: 'image'; src: string; key: string; alt: string }
  | { type: 'video'; url: string; displayMode: string; key: string }
  | { type: 'text'; content: string; key: string };

const hasImage = (url: string | undefined) => Boolean(url && url.trim().length > 0);

export function VerticalGallery({ pairs, sectionTitle }: VerticalGalleryProps) {
  const monoFont = "'IBM Plex Mono', monospace";

  const items = useMemo<VerticalItem[]>(() => {
    const result: VerticalItem[] = [];

    pairs.forEach((pair, index) => {
      if (pair.video_url) {
        result.push({ type: 'video', url: pair.video_url, displayMode: pair.video_display_mode || 'viewport', key: `${index}-video` });
      } else {
        if (hasImage(pair.left)) {
          result.push({ type: 'image', src: pair.left, key: `${index}-left`, alt: pair.title || sectionTitle || 'Obra do portfólio' });
        }
        if (hasImage(pair.right) && pair.right !== pair.left) {
          result.push({ type: 'image', src: pair.right, key: `${index}-right`, alt: pair.title || sectionTitle || 'Obra do portfólio' });
        }
      }

      if (pair.title) {
        result.push({ type: 'text', content: pair.title, key: `${index}-text` });
      }
    });

    return result;
  }, [pairs, sectionTitle]);

  return (
    <div className="w-screen h-screen bg-background overflow-y-auto">
      <div className="flex flex-col">
        {items.map((item, i) =>
          item.type === 'image' ? (
            <ImageReveal key={item.key} index={i}>
              <img
                {...getResponsiveProps(item.src, '100vw')}
                alt={item.alt}
                className="w-full h-auto object-cover"
                loading={i === 0 ? 'eager' : 'lazy'}
              />
            </ImageReveal>
          ) : item.type === 'video' ? (
            <ImageReveal key={item.key} index={i}>
              <div style={{ width: '100%', aspectRatio: item.displayMode === 'viewport' ? undefined : '16/9', height: item.displayMode === 'viewport' ? '100vh' : 'auto' }}>
                <VideoPlayer url={item.url} displayMode={item.displayMode as 'viewport' | 'background'} />
              </div>
            </ImageReveal>
          ) : (
            <div
              key={item.key}
              style={{
                padding: 32,
                fontFamily: monoFont,
                fontSize: 16,
                fontWeight: 300,
                color: 'hsl(var(--foreground))',
              }}
            >
              {item.content}
            </div>
          ),
        )}
      </div>

      {sectionTitle && (
        <div
          className="fixed z-[50] gallery-title-fade"
          style={{
            bottom: 32,
            left: 32,
            fontFamily: monoFont,
            fontSize: 16,
            fontWeight: 300,
            color: 'hsl(var(--foreground))',
            backgroundColor: 'hsl(var(--background))',
            padding: 8,
          }}
        >
          {sectionTitle}
        </div>
      )}
    </div>
  );
}
